import HistoryPanel from "@/components/HistoryPanel";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, AlertTriangle, FileWarning, Info, User } from "lucide-react";
import { api } from "@/lib/api";
import { useCompany } from "@/context/CompanyContext";
import { OrganIcon } from "@/components/Shared";
import { STATUS } from "@/lib/organs";
import {
  CENS_STATES, CENSUS_TO_UI, ORGAN_FEATURES, FLOW_STEPS, OrganismFigure, AreaCard, SYNC_PENDING,
} from "@/components/CensimentoFigure";
import CensusBasisPanel from "@/components/CensusBasisPanel";

// PEND-PW-01 (GO utente 15.09.2026): display source = proiezione census (organs.census_*).
// knowState() legacy RIMOSSA da questa pagina: MAI authority semantica dello stato Census.
// Fallback esplicito GO §11: "sync_pending" (stato TECNICO UI, non un sesto census state).
function censUiState(organ) {
  return organ.census_state ? CENSUS_TO_UI[organ.census_state] : "sync_pending";
}

const LEFT_AREAS = ["eyes_ears", "nervous_system", "breathing", "immune", "digestive"];
const RIGHT_AREAS = ["brain", "memory", "heart", "circulation", "muscles", "skeleton"];

function AreaRow({ organ, docsCount, state, coverage, qualified, onShowBasis }) {
  const st = CENS_STATES[state] || SYNC_PENDING;
  return (
    <Link to={organ.route} data-testid={`cens-area-mobile-${organ.organ_id}`}
      className="glass rounded-2xl px-4 py-3.5 flex items-center gap-3 hover:border-cyan-400/30 transition-colors">
      <span className="w-9 h-9 rounded-2xl flex items-center justify-center text-cyan-300 shrink-0"
        style={{ background: "rgba(0,229,255,0.08)", border: "1px solid rgba(0,229,255,0.15)" }}>
        <OrganIcon icon={organ.icon} size={16} />
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold text-gray-100 truncate">{organ.business_name || organ.business_function}</div>
        <div className="text-[10px] text-gray-500" data-testid={`cens-coverage-mobile-${organ.organ_id}`}>
          {organ.anatomical_name} · {docsCount} documenti · {coverage == null ? "—" : `${Math.round(coverage)}%`}
          {qualified ? <span className="text-amber-400" data-testid={`cens-qualified-mobile-${organ.organ_id}`}> · qualificata</span> : ""}
        </div>
      </div>
      <span className="inline-flex items-center gap-1.5 shrink-0 text-[10px] font-medium" style={{ color: st.color }}>
        <span className="w-1.5 h-1.5 rounded-full" style={{ background: st.color, boxShadow: `0 0 6px ${st.color}` }} />
        {st.label}
      </span>
      <span role="button" tabIndex={0} aria-label="Perché questa percentuale?"
        data-testid={`cens-basis-open-mobile-${organ.organ_id}`}
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); onShowBasis?.(organ.organ_id); }}
        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); e.stopPropagation(); onShowBasis?.(organ.organ_id); } }}
        className="shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-gray-500 hover:text-cyan-300 transition-colors cursor-pointer"
        style={{ background: "rgba(0,229,255,0.06)", border: "1px solid rgba(0,229,255,0.15)" }}>
        <Info size={11} />
      </span>
      <ChevronRight size={14} className="text-gray-600 shrink-0" />
    </Link>
  );
}

function HealthCard({ organs }) {
  // PEND-PW-01: conteggi dalla proiezione census (census_status), MAI da legacy status
  const { counts, pending } = useMemo(() => {
    const c = { GREEN: 0, YELLOW: 0, RED: 0, GREY: 0 };
    let p = 0;
    for (const o of organs) {
      if (o.census_status && c[o.census_status] !== undefined) c[o.census_status] += 1;
      else p += 1;
    }
    return { counts: c, pending: p };
  }, [organs]);
  return (
    <div className="glass rounded-2xl p-4" data-testid="cens-health-card">
      <div className="flex items-center gap-2 mb-3">
        <OrganIcon icon="Activity" size={15} className="text-cyan-300" />
        <div>
          <div className="text-[12px] font-bold text-gray-100 uppercase tracking-wide">Stato di salute</div>
          <div className="text-[10px] font-medium" style={{ color: "#00E5FF" }}>Dashboard</div>
        </div>
      </div>
      <div className="space-y-2">
        {[["GREEN", "Verde"], ["YELLOW", "Giallo"], ["RED", "Rosso"], ["GREY", "Grigio"]].map(([k, name]) => (
          <div key={k} className="flex items-center gap-2.5" data-testid={`cens-health-${k.toLowerCase()}`}>
            <span className="w-3.5 h-3.5 rounded-full shrink-0"
              style={{ background: STATUS[k].color, boxShadow: `0 0 8px ${STATUS[k].glow}` }} />
            <div className="flex-1 min-w-0">
              <span className="text-[11px] font-semibold" style={{ color: STATUS[k].color }}>{name}</span>
              <span className="text-[10px] text-gray-500 ml-1.5">{STATUS[k].label}</span>
            </div>
            <span className="text-[11px] font-bold text-gray-300">{counts[k] || 0}</span>
          </div>
        ))}
      </div>
      {pending > 0 && (
        <p className="text-[10px] mt-2 font-medium" style={{ color: SYNC_PENDING.color }}
          data-testid="cens-health-pending">
          {pending} aree in sincronizzazione
        </p>
      )}
      <p className="text-[10px] text-gray-500 mt-3 leading-relaxed">
        Un unico sguardo ti dice come sta la tua azienda. Clicca e scopri cosa fare.
      </p>
    </div>
  );
}

function DnaCard({ area }) {
  if (!area) return null;
  const feats = ORGAN_FEATURES.dna || [];
  return (
    <Link to={area.organ.route} className="block glass rounded-2xl p-4 hover:border-cyan-400/40 transition-colors"
      data-testid="cens-dna-card">
      <div className="flex items-center gap-2 mb-2.5">
        <OrganIcon icon="Dna" size={15} className="text-cyan-300" />
        <div>
          <div className="text-[12px] font-bold text-gray-100 uppercase tracking-wide">{area.organ.anatomical_name}</div>
          <div className="text-[10px] font-medium" style={{ color: "#00E5FF" }}>
            {area.organ.business_name || area.organ.business_function}
          </div>
        </div>
      </div>
      <ul className="space-y-1">
        {feats.map((f) => (
          <li key={f} className="text-[11px] text-gray-400 flex items-center gap-1.5">
            <span className="w-1 h-1 rounded-full bg-cyan-400/60 shrink-0" />{f}
          </li>
        ))}
      </ul>
      <p className="text-[10px] text-gray-500 mt-2.5 leading-relaxed">
        Contiene il modo unico in cui la tua azienda funziona.
      </p>
    </Link>
  );
}

function ConsciousnessCard() {
  return (
    <Link to="/actions" className="block glass rounded-2xl p-4 hover:border-cyan-400/40 transition-colors"
      data-testid="cens-consciousness-card">
      <div className="flex items-center gap-2 mb-2.5">
        <User size={15} className="text-cyan-300" />
        <div>
          <div className="text-[12px] font-bold text-gray-100 uppercase tracking-wide">Coscienza</div>
          <div className="text-[10px] font-medium" style={{ color: "#00E5FF" }}>Imprenditore</div>
        </div>
      </div>
      <ul className="space-y-1">
        {["Decide e autorizza", "Definisce obiettivi", "Dà la direzione"].map((f) => (
          <li key={f} className="text-[11px] text-gray-400 flex items-center gap-1.5">
            <span className="w-1 h-1 rounded-full bg-cyan-400/60 shrink-0" />{f}
          </li>
        ))}
      </ul>
      <p className="text-[10px] text-gray-300 mt-3 leading-relaxed italic border-l-2 pl-2.5"
        style={{ borderColor: "rgba(0,229,255,0.35)" }}>
        "Il sistema può vedere, comprendere e proporre. Ma le decisioni importanti rimangono umane."
      </p>
    </Link>
  );
}

function FlowStrip() {
  return (
    <div className="glass rounded-3xl p-5 mt-8 fade-up" data-testid="cens-flow-strip">
      <p className="text-[10px] tracking-[0.3em] text-gray-500 mb-4">IL FLUSSO DELLE INFORMAZIONI</p>
      <div className="flex flex-wrap items-center justify-center gap-y-4">
        {FLOW_STEPS.map((s, i) => (
          <div key={s.id} className="flex items-center" data-testid={`cens-flow-${s.id}`}>
            <div className="flex flex-col items-center text-center w-[92px]">
              <span className="w-9 h-9 rounded-full flex items-center justify-center text-cyan-300"
                style={{ background: "rgba(0,229,255,0.07)", border: "1px solid rgba(0,229,255,0.18)" }}>
                <OrganIcon icon={s.icon} size={15} />
              </span>
              <span className="text-[10.5px] font-bold text-gray-200 uppercase tracking-wide mt-2">{s.label}</span>
              <span className="text-[9px] text-gray-500 leading-tight mt-0.5">{s.sub}</span>
            </div>
            {i < FLOW_STEPS.length - 1 && <ChevronRight size={13} className="text-gray-600 mx-1 shrink-0" />}
          </div>
        ))}
      </div>
    </div>
  );
}

// Linee di connessione card ↔ organo, misurate sul DOM reale (curva elegante DAD)
function Connectors({ containerRef, byId, ids }) {
  const [paths, setPaths] = useState([]);
  useLayoutEffect(() => {
    const measure = () => {
      const c = containerRef.current;
      if (!c) return;
      const cr = c.getBoundingClientRect();
      const out = [];
      for (const id of ids) {
        const card = c.querySelector(`[data-testid="cens-area-${id}"]`);
        const hot = c.querySelector(`[data-testid="cens-hotspot-${id}"]`);
        if (!card || !hot || !byId[id]) continue;
        const a = card.getBoundingClientRect();
        const b = hot.getBoundingClientRect();
        const fromLeft = a.left < b.left;
        const x1 = (fromLeft ? a.right : a.left) - cr.left;
        const y1 = a.top - cr.top + a.height / 2;
        const x2 = b.left - cr.left + b.width / 2;
        const y2 = b.top - cr.top + b.height / 2;
        const mx = (x1 + x2) / 2;
        out.push({
          id,
          d: `M${x1},${y1} C ${mx},${y1} ${mx},${y2} ${x2},${y2}`,
          x2, y2,
          color: (CENS_STATES[byId[id].state] || CENS_STATES.non_analizzata).color,
        });
      }
      setPaths(out);
    };
    const raf = requestAnimationFrame(measure);
    const t1 = setTimeout(measure, 350);
    const t2 = setTimeout(measure, 1200);
    window.addEventListener("resize", measure);
    window.addEventListener("load", measure);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(t1);
      clearTimeout(t2);
      window.removeEventListener("resize", measure);
      window.removeEventListener("load", measure);
    };
  }, [containerRef, byId, ids]);
  return (
    <svg className="absolute inset-0 w-full h-full pointer-events-none" data-testid="cens-connectors">
      {paths.map((p) => (
        <g key={p.id}>
          <path d={p.d} fill="none" stroke={p.color} strokeOpacity="0.5" strokeWidth="1.4"
            style={{ filter: `drop-shadow(0 0 4px ${p.color}66)` }} />
          <circle cx={p.x2} cy={p.y2} r="2.4" fill={p.color}
            style={{ filter: `drop-shadow(0 0 5px ${p.color})` }} />
        </g>
      ))}
    </svg>
  );
}

export default function Censimento() {
  const { company, companyId } = useCompany();
  const navigate = useNavigate();
  const sceneRef = useRef(null);
  const [organs, setOrgans] = useState([]);
  const [docs, setDocs] = useState([]);
  const [actions, setActions] = useState([]);
  const [basisOrganId, setBasisOrganId] = useState(null);

  useEffect(() => {
    const controller = new AbortController();
    setOrgans([]); setDocs([]); setActions([]); setBasisOrganId(null);
    if (!companyId) return () => controller.abort();
    const config = { signal: controller.signal, headers: { "x-company-id": companyId } };
    api.get("/organs", config).then((r) => { if (!controller.signal.aborted) setOrgans(r.data); }).catch(() => {});
    api.get("/documents", config).then((r) => { if (!controller.signal.aborted) setDocs(r.data); }).catch(() => {});
    api.get("/actions", config).then((r) => { if (!controller.signal.aborted) setActions(r.data.filter((a) => a.status === "open")); }).catch(() => {});
    return () => controller.abort();
  }, [companyId]);

  const docsPerOrgan = useMemo(() => {
    const map = {};
    for (const d of docs) for (const o of d.organs || []) map[o] = (map[o] || 0) + 1;
    return map;
  }, [docs]);

  const areas = organs.map((o) => ({
    organ: o,
    docsCount: docsPerOrgan[o.organ_id] || 0,
    state: censUiState(o),
    coverage: o.census_coverage ?? null,  // UNKNOWN ≠ 0%: mai fallback numerico inventato (GO §5)
    qualified: !!o.census_qualified,
  }));
  const byId = Object.fromEntries(areas.map((a) => [a.organ.organ_id, a]));
  const counts = useMemo(() => {
    const c = {};
    for (const a of areas) c[a.state] = (c[a.state] || 0) + 1;
    return c;
  }, [areas]);
  const censite = counts.censita || 0;
  const exceptions = docs.filter((d) => ["to_confirm", "anomaly", "failed"].includes(d.status));

  const onOpen = (id) => {
    if (id === "consciousness") return navigate("/actions");
    if (byId[id]) navigate(byId[id].organ.route);
  };

  const legend = (testid, extraClass) => (
    <div className={`flex-wrap justify-center gap-2 ${extraClass}`} data-testid={testid}>
      {Object.entries(CENS_STATES).map(([k, v]) => (
        <span key={k} className="chip" style={{ color: v.color, borderColor: `${v.color}35`, background: `${v.color}10` }}
          data-testid={`cens-legend-${k}`}>
          <span className="w-1.5 h-1.5 rounded-full" style={{ background: v.color }} />
          {v.label}{counts[k] ? ` · ${counts[k]}` : ""}
        </span>
      ))}
    </div>
  );

  return (
    <div className="max-w-[1600px] mx-auto px-4 pb-24 lg:pb-8" data-testid="censimento-page">
      <HistoryPanel/>
      <Link to="/" className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-cyan-300 mb-4">
        <ChevronLeft size={14} /> Organismo
      </Link>

      <div className="text-center mb-6 fade-up">
        <p className="text-xs tracking-[0.3em] text-gray-500">CENSIMENTO AZIENDALE · AUDIT FIRST</p>
        <h1 className="font-display text-3xl sm:text-4xl font-bold mt-2 uppercase" data-testid="cens-title">
          L'azienda come un <span style={{ color: "#00E5FF" }}>organismo</span>
        </h1>
        <p className="text-sm text-gray-500 mt-2">{company?.name} — Un unico organismo. Un'azienda più forte.</p>
        <p className="text-xs text-gray-400 mt-1" data-testid="cens-general-state">
          Conoscenza aziendale: <span className="text-emerald-400 font-semibold">{organs.length ? `${censite}/${organs.length} aree restituite censite` : "Dati insufficienti"}</span>
          {counts.anomalie ? <span className="text-red-400 font-semibold"> · {counts.anomalie} anomalie</span> : ""}
          {exceptions.length ? <span className="text-amber-400"> · {exceptions.length} documenti da verificare</span> : ""}
          {counts.sync_pending ? <span className="font-semibold" style={{ color: SYNC_PENDING.color }}> · {counts.sync_pending} in sincronizzazione</span> : ""}
        </p>
      </div>

      {legend("cens-legend", "hidden lg:flex mb-6 fade-up")}

      {/* Desktop: radiografia completa — figura centrale, aree collegate, colonna salute */}
      <div ref={sceneRef} className="hidden lg:grid lg:grid-cols-[230px_minmax(0,1fr)_230px] xl:grid-cols-[280px_minmax(0,1fr)_280px_260px] gap-10 items-start fade-up relative"
        data-testid="cens-desktop-scene">
        <Connectors containerRef={sceneRef} byId={byId} ids={[...LEFT_AREAS, ...RIGHT_AREAS]} />
        <div className="space-y-3 pt-2">
          {LEFT_AREAS.map((id) => byId[id] && <AreaCard key={id} area={byId[id]} side="left" onShowBasis={setBasisOrganId} />)}
        </div>
        <div className="w-full max-w-[600px] mx-auto">
          <OrganismFigure areas={areas} onOpen={onOpen} />
          <p className="text-center text-[11px] text-gray-500 mt-2">Passa sugli organi e clicca per aprire l'area</p>
        </div>
        <div className="space-y-3 pt-2">
          {RIGHT_AREAS.map((id) => byId[id] && <AreaCard key={id} area={byId[id]} side="right" onShowBasis={setBasisOrganId} />)}
        </div>
        <div className="hidden xl:block space-y-3 opacity-90">
          <HealthCard organs={organs} />
          <DnaCard area={byId.dna} />
          <ConsciousnessCard />
        </div>
      </div>

      {/* Sidebar salute in riga per schermi lg (non xl) */}
      <div className="hidden lg:grid xl:hidden grid-cols-3 gap-3 mt-5 fade-up opacity-90">
        <HealthCard organs={organs} />
        <DnaCard area={byId.dna} />
        <ConsciousnessCard />
      </div>

      {/* Smartphone: composizione verticale dedicata — figura protagonista */}
      <div className="lg:hidden space-y-5">
        <div className="glass rounded-3xl p-3 fade-up" data-testid="cens-mobile-figure">
          <OrganismFigure areas={areas} onOpen={onOpen} showLabel />
          <p className="text-center text-[11px] text-gray-500 mt-2">Tocca un organo per aprire l'area</p>
        </div>

        {legend("cens-legend-mobile", "flex fade-up")}

        <div className="space-y-2.5" data-testid="cens-mobile-areas">
          {areas.map((a) => <AreaRow key={a.organ.organ_id} {...a} onShowBasis={setBasisOrganId} />)}
        </div>

        <div className="grid grid-cols-1 gap-3">
          <HealthCard organs={organs} />
          <DnaCard area={byId.dna} />
          <ConsciousnessCard />
        </div>
      </div>

      <Link to="/marketing" className="dad-panel dad-marketing-census"><strong>BOCCA · Marketing & Comunicazione</strong><span>Area in sviluppo · Dati insufficienti. Modulo canonico 13, non incluso nelle proiezioni backend.</span></Link>
      <FlowStrip />

      {/* Azioni / documenti mancanti / anomalie */}
      <div className="grid sm:grid-cols-3 gap-3 mt-6 fade-up" data-testid="cens-bottom-strip">
        <Link to="/actions" data-testid="cens-goto-actions" className="glass rounded-3xl p-4 flex items-center gap-3 hover:border-red-400/30 transition-colors">
          <AlertTriangle size={18} className="text-red-400 shrink-0" />
          <div>
            <div className="font-display font-bold text-lg">{actions.length}</div>
            <div className="text-[11px] text-gray-500">Decisioni / eccezioni aperte</div>
          </div>
        </Link>
        <Link to="/queue" data-testid="cens-goto-queue" className="glass rounded-3xl p-4 flex items-center gap-3 hover:border-amber-400/30 transition-colors">
          <FileWarning size={18} className="text-amber-400 shrink-0" />
          <div>
            <div className="font-display font-bold text-lg">{exceptions.length}</div>
            <div className="text-[11px] text-gray-500">Documenti da verificare</div>
          </div>
        </Link>
        <Link to="/scan" data-testid="cens-scan-cta" className="glass rounded-3xl p-4 flex items-center gap-3 hover:border-cyan-400/40 transition-colors">
          <span className="font-display font-bold text-lg text-cyan-300">SCAN</span>
          <div className="text-[11px] text-gray-500">Alimenta il censimento con nuovi documenti</div>
        </Link>
      </div>

      {basisOrganId && (
        <CensusBasisPanel organId={basisOrganId}
          organName={byId[basisOrganId]?.organ.business_name || byId[basisOrganId]?.organ.business_function}
          onClose={() => setBasisOrganId(null)} />
      )}
    </div>
  );
}
